export type Locale =
  | 'af'
  | 'sq'
  | 'ar-SA'
  | 'ar-IQ'
  | 'ar-EG'
  | 'ar-LY'
  | 'ar-DZ'
  | 'ar-MA'
  | 'ar-TN'
  | 'ar-OM'
  | 'ar-YE'
  | 'ar-SY'
  | 'ar-JO'
  | 'ar-LB'
  | 'ar-KW'
  | 'ar-AE'
  | 'ar-BH'
  | 'ar-QA'
  | 'eu'
  | 'bg'
  | 'be'
  | 'ca'
  | 'zh-TW'
  | 'zh-CN'
  | 'zh-HK'
  | 'zh-SG'
  | 'hr'
  | 'cs'
  | 'da'
  | 'nl'
  | 'nl-BE'
  | 'en'
  | 'en-US'
  | 'en-EG'
  | 'en-AU'
  | 'en-GB'
  | 'en-CA'
  | 'en-NZ'
  | 'en-IE'
  | 'en-ZA'
  | 'en-JM'
  | 'en-BZ'
  | 'en-TT'
  | 'et'
  | 'fo'
  | 'fa'
  | 'fi'
  | 'fr'
  | 'fr-BE'
  | 'fr-CA'
  | 'fr-CH'
  | 'fr-LU'
  | 'gd'
  | 'gd-IE'
  | 'de'
  | 'de-CH'
  | 'de-AT'
  | 'de-LU'
  | 'de-LI'
  | 'el'
  | 'he'
  | 'hi'
  | 'hu'
  | 'is'
  | 'id'
  | 'it'
  | 'it-CH'
  | 'ja'
  | 'ko'
  | 'lv'
  | 'lt'
  | 'mk'
  | 'mt'
  | 'no'
  | 'pl'
  | 'pt-BR'
  | 'pt'
  | 'rm'
  | 'ro'
  | 'ro-MO'
  | 'ru'
  | 'ru-MI'
  | 'sz'
  | 'sr'
  | 'sk'
  | 'sl'
  | 'sb'
  | 'es'
  | 'es-AR'
  | 'es-GT'
  | 'es-CR'
  | 'es-PA'
  | 'es-DO'
  | 'es-MX'
  | 'es-VE'
  | 'es-CO'
  | 'es-PE'
  | 'es-EC'
  | 'es-CL'
  | 'es-UY'
  | 'es-PY'
  | 'es-BO'
  | 'es-SV'
  | 'es-HN'
  | 'es-NI'
  | 'es-PR'
  | 'sx'
  | 'sv'
  | 'sv-FI'
  | 'th'
  | 'ts'
  | 'tn'
  | 'tr'
  | 'uk'
  | 'ur'
  | 've'
  | 'vi'
  | 'xh'
  | 'ji'
  | 'zu';

/**
 * Derzeit unterstützte Sprachen
 * ACHTUNG: Reihenfolge wichtig!
 *
 * @type {string[]}
 */
const supportedLocales: Locale[] = ['en', 'de', 'es'];

/**
 * Identifiziert die wahrscheinlichste Browsersprache
 *
 * Ist nur eine Sprache gesetzt, wird diese verwendet.
 * Sind mehrere Browsersprachen gleichzeitig aktiv,
 * so wird der Reihenfolge der unterstützten Sprachen nach geschaut,
 * ob diese Sprache in den Browsersprachen vorkommt und wenn ja, aktiviert.
 *
 * Letzter Fallback ist Englisch.
 *
 * @returns {string}
 */
const getLocale = (): string => {
  let locale: Locale = 'en';
  let n: any = navigator as any;

  if (n.language) {
    // latest versions of Chrome, Firefox, and Safari set this correctly
    locale = n.language;
  } else if (n.languages && n.languages.length > 0) {
    // latest versions of Chrome and Firefox set this correctly
    locale = n.languages[0];
  } else if (n.userLanguage) {
    // IE only
    locale = n.userLanguage;
  }
  // Mapped zB. "de-DE" auf "de"
  locale = locale.substr(0, 2) as Locale;

  // Fallback auf zweite Sprache wenn möglich
  if (supportedLocales.indexOf(locale) < 0 && n.languages && n.languages.length > 0) {
    const browserLanguages: Locale[] = n.languages.map((l: Locale) => l.substr(0, 2));
    const locales: Locale[] = supportedLocales.filter((l: Locale) => browserLanguages.indexOf(l) >= 0);

    if (locales.length > 0) {
      locale = locales[0].substr(0, 2) as Locale;
    }
  }

  // Fallback auf Englisch
  return supportedLocales.indexOf(locale) >= 0 ? locale : 'en';
};

export const locale = getLocale();

/**
 * Ausgabe eines Lingua-Textes.
 *
 * @param id Text-ID, z.B. "IDC_STUDENT".
 * @param args Optionale Parameter für den Text, falls solche Parameter im Text definiert sind,
 * z.B. "Es wurden {0} Schüler importiert.", wobei die Parameter als Array übergeben und per Index
 * referenziert werden.
 * @return {string} Der Text in der jeweiligen Sprache, der Text auf Deutsch oder die übergebene ID,
 * falls der Text nicht definiert ist.
 */
export const text = (id: string, args?: any[]): string => {
  return (texts[locale] && texts[locale][id]) || id;
};

/**
 * Texte zur Übersetzung
 */
const texts = {
  de: {
    LBL_SCHOOL_SEARCH_TITLE: 'Schulsuche',
    LBL_WELCOME: 'Willkommen bei WebUntis',
    LBL_LEAD: 'Bitte suchen Sie Ihre Schule',
    LBL_LEAD_SMARTPHONE: 'WebUntis funktioniert am Besten, wenn Sie die App installieren',
    LBL_IMPRESS: 'Impressum',
    LBL_LEGAL: 'Datenschutz',
    LBL_PLACEHOLDER: 'z.B. Schulname, Stadt, Adresse',
    LBL_LOADING: 'Wird geladen...',
    LBL_NO_WEBUNTIS: 'Meine Schule verwendet WebUntis noch nicht',
    LBL_REFINE_SEARCH: 'Bitte schränken Sie Ihre Suche weiter ein!',
    LBL_TOO_MANY_SCHOOLS: 'Es wurden zu viele Schulen gefunden...',
    LBL_SCHOOL_NOT_FOUND: 'Ihre Schule wurde nicht gefunden...',
    LBL_CONTACT_SCHOOL_ADMIN: 'Kontaktieren Sie Ihre Schulverwaltung oder besuchen Sie unsere Webseite!',
    LBL_USE_BROWSER: 'Im Browser verwenden',
    LBL_JOBS_AT_UNTIS: 'Jobs @ Untis',
    LBL_OR: 'oder',
    LBL_REMOVE_RECENT_SEARCH_ITEM: 'Letzten Suchbegriff löschen',
    LBL_SCHOOL_NOT_FOUND_HEADER: 'Ihr WebUntis hat ein neues Zuhause!',
    LBL_SCHOOL_NOT_FOUND_HINT: 'So finden Sie Ihre Schule wieder',
    LBL_SCHOOL_NOT_FOUND_STEP_1: 'Geben Sie unten den Schulnamen oder die Adresse ein',
    LBL_SCHOOL_NOT_FOUND_STEP_2: 'Wählen Sie Ihre Schule aus der Liste',
    LBL_SCHOOL_NOT_FOUND_TIP: 'Tip',
    LBL_SCHOOL_NOT_FOUND_TIP_1: 'Speichern Sie anschließend die neue WebUntis-Seite Ihrer Schule als Favorit - um künftig mit einem Klick darauf zugreifen zu können.',
    LBL_SCHOOL_NOT_FOUND_TIP_2: 'z. B. mit <kbd>Strg + D</kbd> oder <kbd>Cmd + D</kbd>',
  },
  en: {
    LBL_SCHOOL_SEARCH_TITLE: 'School Search',
    LBL_WELCOME: 'Welcome to WebUntis',
    LBL_LEAD: 'Please search for your school',
    LBL_LEAD_SMARTPHONE: 'To get the best WebUntis experience please download our app',
    LBL_IMPRESS: 'Impressum',
    LBL_LEGAL: 'Privacy policy',
    LBL_PLACEHOLDER: 'Search for School Name, City or Address',
    LBL_LOADING: 'Loading...',
    LBL_NO_WEBUNTIS: "My school doesn't use WebUntis yet",
    LBL_REFINE_SEARCH: 'Please refine your search!',
    LBL_TOO_MANY_SCHOOLS: 'Too many schools match your search...',
    LBL_SCHOOL_NOT_FOUND: 'Your school is not found...',
    LBL_CONTACT_SCHOOL_ADMIN: 'Please contact your school administration or visit our website!',
    LBL_USE_BROWSER: 'Open in browser',
    LBL_JOBS_AT_UNTIS: 'Jobs @ Untis',
    LBL_OR: 'or',
    LBL_REMOVE_RECENT_SEARCH_ITEM: 'Remove recent search item',
    LBL_SCHOOL_NOT_FOUND_HEADER: 'Your WebUntis has a new home!',
    LBL_SCHOOL_NOT_FOUND_HINT: 'Here’s how to find your school again',
    LBL_SCHOOL_NOT_FOUND_STEP_1: 'Enter your school name or address in the search field below',
    LBL_SCHOOL_NOT_FOUND_STEP_2: 'Select your school from the list',
    LBL_SCHOOL_NOT_FOUND_TIP: 'Tip',
    LBL_SCHOOL_NOT_FOUND_TIP_1: 'Afterwards, save your school’s new WebUntis page as a favorite – so you can access it with just one click next time.',
    LBL_SCHOOL_NOT_FOUND_TIP_2: 'e.g. by pressing <kbd>Ctrl + D</kbd> or <kbd>Cmd + D</kbd>',
  },
  es: {
    LBL_SCHOOL_SEARCH_TITLE: 'Búsqueda de escuela',
    LBL_WELCOME: 'Bienvenido a WebUntis',
    LBL_LEAD: 'Por favor busque su centro',
    LBL_LEAD_SMARTPHONE: 'Para una mejor experiencia, se puede descargar la aplicación para móviles',
    LBL_IMPRESS: 'Aviso legal',
    LBL_LEGAL: 'Privacy policy',
    LBL_PLACEHOLDER: 'p.e. nombre de centro, municipio, dirección',
    LBL_LOADING: 'Cargando...',
    LBL_NO_WEBUNTIS: 'Mi centro todavía no utiliza WebUntis',
    LBL_REFINE_SEARCH: 'Por favor refine su búsqueda!',
    LBL_TOO_MANY_SCHOOLS: 'Demasiados centros encontrados...',
    LBL_SCHOOL_NOT_FOUND: 'Su centro no fue encontrado...',
    LBL_CONTACT_SCHOOL_ADMIN: 'Por favor contacte con su administrador o visite nuestra página web!',
    LBL_USE_BROWSER: 'Utilizar en navegador web',
    LBL_JOBS_AT_UNTIS: 'Jobs @ Untis',
    LBL_OR: 'o',
    LBL_REMOVE_RECENT_SEARCH_ITEM: 'Eliminar última búsqueda',
    LBL_SCHOOL_NOT_FOUND_HEADER: '¡Su WebUntis tiene un nuevo hogar!',
    LBL_SCHOOL_NOT_FOUND_HINT: 'Así puede volver a encontrar su escuela',
    LBL_SCHOOL_NOT_FOUND_STEP_1: 'Escriba abajo el nombre de su escuela o la dirección',
    LBL_SCHOOL_NOT_FOUND_STEP_2: 'Seleccione su escuela de la lista',
    LBL_SCHOOL_NOT_FOUND_TIP: 'Consejo',
    LBL_SCHOOL_NOT_FOUND_TIP_1: 'Guarde después la nueva página de WebUntis de su escuela en favoritos para poder acceder con un solo clic la próxima vez.',
    LBL_SCHOOL_NOT_FOUND_TIP_2: 'p. ej. con <kbd>Ctrl + D</kbd> or <kbd>Cmd + D</kbd>',
  },
};



// WEBPACK FOOTER //
// ./src/common/text.ts